import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { Home, Salad, NotebookText, LifeBuoy, Menu as MenuIcon, User } from 'lucide-react'
import { supabase } from './lib/supabase'
import { invocarFuncion } from './lib/invocarFuncion'
import { WorkspaceProvider, useWorkspace } from './lib/WorkspaceContext'
import { MENSAJE_ERROR_INVITACION } from './lib/invitaciones'
import { obtenerSlugDeSubdominio } from './lib/subdominio'
import Auth from './pages/Auth'
import ActualizarPassword from './pages/ActualizarPassword'
import Landing from './pages/Landing'
import Inicio from './pages/Inicio'
import Mealprep from './pages/Mealprep'
import Progreso from './pages/Progreso'
import Clases from './pages/Clases'
import Diario from './pages/Diario'
import Habitos from './pages/Habitos'
import Accesos from './pages/Accesos'
import PlanLineamientos from './pages/PlanLineamientos'
import Perfil from './pages/Perfil'
import ConfiguracionPaciente from './pages/ConfiguracionPaciente'
import Receta from './pages/Receta'
import Privacidad from './pages/Privacidad'
import BorrarCuenta from './pages/BorrarCuenta'
import AdminRecetarios from './pages/AdminRecetarios'
import SelectorWorkspace from './pages/SelectorWorkspace'
import Sos from './pages/Sos'
import Reportes from './pages/Reportes'
import Turnos from './pages/Turnos'
import './App.css'

// Lazy: el bundle de react-router-dom + AdminApp nunca debería viajar al
// celular de un paciente (mayoría de los usuarios) — solo se descarga si
// el gate de rol de abajo realmente lo necesita.
const AdminApp = lazy(() => import('./pages/admin/AdminApp'))

export default function App() {
  const [usuario, setUsuario] = useState(null)
  const [cargandoSesion, setCargandoSesion] = useState(true)
  const [modoRecuperacion, setModoRecuperacion] = useState(false)
  const [tab, setTab] = useState('inicio')
  const [recetaSeleccionada, setRecetaSeleccionada] = useState(null)
  const [listaCompras, setListaCompras] = useState([])
  const [menuRecetas, setMenuRecetas] = useState([])
  const [favoritos, setFavoritos] = useState(new Set())
  const [favoritosDetalle, setFavoritosDetalle] = useState([])
  const [historial, setHistorial] = useState([])
  const [trigNuevoRegistroProgreso, setTrigNuevoRegistroProgreso] = useState(0)

  // Bloque 2.1b: recepción del link de invitación por mail (reemplaza el
  // código tipeado por completo — ver docs/juco-plan-master.md Sección 7.5).
  // Se lee una sola vez al montar, sin sumar router para esto.
  const [codigoInvitacion, setCodigoInvitacion] = useState(
    () => new URLSearchParams(window.location.search).get('invitacion')
  )
  const [procesandoInvitacion, setProcesandoInvitacion] = useState(!!codigoInvitacion)
  const [errorInvitacion, setErrorInvitacion] = useState(null)
  const [intentoInvitacion, setIntentoInvitacion] = useState(0)
  const [nombreInvitacion, setNombreInvitacion] = useState('')

  // #7 (Bloque F): precargar el campo "Nombre" del registro con el que ya
  // cargó quien invita — corre antes de loguearse (redimir_invitacion recién
  // corre después), así que necesita su propio lookup público por código
  // exacto, mismo patrón anti-directorio que resolver_workspace_por_slug.
  useEffect(() => {
    if (!codigoInvitacion || usuario) return
    supabase.rpc('obtener_datos_invitacion', { p_codigo: codigoInvitacion }).then(({ data }) => {
      if (data && data.length > 0 && data[0].nombre) setNombreInvitacion(data[0].nombre)
    })
  }, [codigoInvitacion, usuario])

  const limpiarCodigoInvitacion = () => {
    setCodigoInvitacion(null)
    const url = new URL(window.location.href)
    url.searchParams.delete('invitacion')
    window.history.replaceState({}, '', url)
  }

  // Corre apenas hay sesión Y un código pendiente — antes de que
  // WorkspaceProvider/AppLogueado se monten (ver gate en el return de más
  // abajo), así la fila de workspace_miembros ya existe cuando
  // WorkspaceContext.cargarWorkspaces() corra por primera vez, sin carrera
  // entre las dos.
  useEffect(() => {
    if (!usuario || !codigoInvitacion) return
    let cancelado = false

    ;(async () => {
      const { error } = await supabase.rpc('redimir_invitacion', { p_codigo: codigoInvitacion })
      if (cancelado) return

      if (error) {
        if (error.message === 'email_no_coincide') {
          setErrorInvitacion({ tipo: 'email_no_coincide' })
          limpiarCodigoInvitacion()
        } else if (error.message in MENSAJE_ERROR_INVITACION) {
          setErrorInvitacion({ tipo: 'terminal', mensaje: MENSAJE_ERROR_INVITACION[error.message] })
          limpiarCodigoInvitacion()
        } else {
          // Error no reconocido (de red, etc.) — no se limpia el código,
          // para poder reintentar sin perder la invitación.
          setErrorInvitacion({ tipo: 'reintentable', mensaje: 'No se pudo procesar la invitación. Reintentá.' })
        }
        setProcesandoInvitacion(false)
        return
      }

      limpiarCodigoInvitacion()
      setProcesandoInvitacion(false)
    })()

    return () => { cancelado = true }
  }, [usuario, codigoInvitacion, intentoInvitacion])

  // #15/#19 (Bloque 3.3): supabase-js refresca el token solo al volver de
  // background/cambiar de pestaña (visibilitychange), y eso dispara este
  // mismo evento con una sesión igual de válida — no es un login nuevo. Sin
  // este chequeo, cada refresh de token creaba un objeto `usuario` nuevo,
  // lo que le cambiaba la identidad al `useCallback` de `cargarWorkspaces`
  // en WorkspaceContext, reactivaba su efecto, y `cargandoWorkspace` volvía
  // a `true` — App.js tiene un `if (cargandoWorkspace) return <Cargando/>`
  // que desmonta TODO el árbol (formularios de Planes/Clases incluidos) y
  // lo vuelve a montar de cero al terminar. La sesión nunca se perdía (por
  // eso no se notaba un reload real) pero cualquier estado local en un
  // formulario sí. Confirmado con Julieta: solo el form quedaba vacío, el
  // resto de la app seguía exactamente donde estaba. Reproducido y
  // verificado con un refreshSession() real (no simulado): sin este guard,
  // el form se vacía; con el guard, sobrevive — mismo evento TOKEN_REFRESHED
  // real en los dos casos.
  const usuarioIdRef = useRef(null)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        usuarioIdRef.current = session.user.id
        setUsuario(session.user)
        cargarDatosUsuario(session.user)
      }
      setCargandoSesion(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // Al volver del link de "olvidé mi contraseña", supabase-js detecta el
      // token en la URL solo (detectSessionInUrl, default en el cliente) y
      // dispara este evento con una sesión ya activa. Si no lo interceptamos
      // acá, el usuario cae derecho al home logueado en vez de a la pantalla
      // para poner la contraseña nueva.
      if (_event === 'PASSWORD_RECOVERY') {
        setModoRecuperacion(true)
        return
      }

      if (session?.user) {
        if (session.user.id === usuarioIdRef.current) return // mismo usuario, solo refresh de token
        usuarioIdRef.current = session.user.id
        setUsuario(session.user)
        cargarDatosUsuario(session.user)
      } else {
        usuarioIdRef.current = null
        setUsuario(null)
        setFavoritos(new Set())
        setHistorial([])
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const cargarDatosUsuario = async (usuario) => {
    const userId = usuario.id

    // Asegurar perfil en tabla usuarios (el upsert en el registro falla si el email
    // todavía no está confirmado, porque en ese momento no hay sesión activa)
    await supabase.from('usuarios').upsert({
      id: userId,
      email: usuario.email,
      nombre: usuario.user_metadata?.nombre || usuario.email?.split('@')[0]
    })

    const [{ data: favs }, { data: hist }] = await Promise.all([
      supabase.from('favoritos')
        .select('receta_id, recetas(id, nombre, tipo_comida, tiempo_prep_min, apta_vegana, apta_sin_gluten, dulzor)')
        .eq('usuario_id', userId),
      supabase.from('historial_recetas')
        .select('receta_id, fecha, recetas(id, nombre, tipo_comida, tiempo_prep_min)')
        .eq('usuario_id', userId)
        .order('fecha', { ascending: false })
        .limit(50)
    ])

    if (favs) {
      setFavoritos(new Set(favs.map(f => f.receta_id)))
      setFavoritosDetalle(favs.map(f => f.recetas).filter(Boolean))
    }
    if (hist) {
      setHistorial(hist.map(h => ({
        ...h.recetas,
        fecha: new Date(h.fecha).toLocaleDateString('es-AR')
      })))
    }
  }

  const toggleFavorito = async (receta) => {
    if (!usuario) return
    const recetaId = typeof receta === 'object' ? receta.id : receta
    const esFav = favoritos.has(recetaId)
    if (esFav) {
      await supabase.from('favoritos').delete().eq('usuario_id', usuario.id).eq('receta_id', recetaId)
      setFavoritos(prev => { const next = new Set(prev); next.delete(recetaId); return next })
      setFavoritosDetalle(prev => prev.filter(r => r.id !== recetaId))
    } else {
      await supabase.from('favoritos').insert({ usuario_id: usuario.id, receta_id: recetaId })
      setFavoritos(prev => new Set([...prev, recetaId]))
      if (typeof receta === 'object') setFavoritosDetalle(prev => [...prev, receta])
    }
  }

  const marcarRealizada = async (receta) => {
    if (!usuario) return
    await supabase.from('historial_recetas').insert({
      usuario_id: usuario.id, receta_id: receta.id, fecha: new Date().toISOString()
    })
    setHistorial(prev => [{ ...receta, fecha: new Date().toLocaleDateString('es-AR') }, ...prev])
  }

  const agregarACompras = (ingredientes, recetaNombre) => {
    setListaCompras(prev => {
      const existentes = prev.filter(p => p.origen !== recetaNombre)
      const nuevos = ingredientes.map(i => ({ ...i, origen: recetaNombre, id: `${i.ingrediente_id}-${recetaNombre}` }))
      return [...existentes, ...nuevos]
    })
  }

  const removerReceta = (recetaNombre) => {
    setListaCompras(prev => prev.filter(i => i.origen !== recetaNombre))
    setMenuRecetas(prev => prev.filter(r => r.nombre !== recetaNombre))
  }

  const agregarAlMenu = (receta) => {
    setMenuRecetas(prev => prev.find(r => r.id === receta.id) ? prev : [...prev, receta])
  }

  const removerDelMenu = (recetaId) => {
    const receta = menuRecetas.find(r => r.id === recetaId)
    setMenuRecetas(prev => prev.filter(r => r.id !== recetaId))
    if (receta) setListaCompras(prev => prev.filter(i => i.origen !== receta.nombre))
  }

  const verReceta = (receta) => setRecetaSeleccionada(receta)
  const volver = () => setRecetaSeleccionada(null)

  const handleLogout = async () => {
    await supabase.auth.signOut()
    setUsuario(null)
    setListaCompras([])
    setMenuRecetas([])
    setFavoritos(new Set())
    setHistorial([])
    setTab('inicio')
  }

  const handleEliminarCuenta = async () => {
    const { error } = await invocarFuncion('eliminar-cuenta')
    if (error) { alert('No se pudo eliminar la cuenta. Intentá de nuevo.'); return }
    await handleLogout()
  }

  // Rutas públicas, accesibles sin sesión (requisito de las tiendas: política de privacidad y borrado de cuenta vía web)
  if (window.location.pathname === '/privacidad') return <Privacidad />
  if (window.location.pathname === '/borrar-cuenta') return <BorrarCuenta />

  if (cargandoSesion) {
    return (
      <div className="app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--gris)', fontSize: 14 }}>Cargando...</p>
      </div>
    )
  }

  if (modoRecuperacion) {
    return (
      <ActualizarPassword
        onListo={(user) => {
          setModoRecuperacion(false)
          setUsuario(user)
          cargarDatosUsuario(user)
        }}
      />
    )
  }

  // Landing en "/" solo para visitantes sin sesión en el dominio pelado
  // (sin subdominio de workspace) — cada workspace sigue entrando por su
  // propio subdominio directo al login, como siempre. El login se mudó a
  // "/login"; cualquier otra ruta sin sesión (incluida "/login") sigue
  // cayendo en Auth, igual que antes.
  if (!usuario) {
    if (window.location.pathname === '/' && !obtenerSlugDeSubdominio()) return <Landing />
    return <Auth onLogin={(user) => setUsuario(user)} invitacionPendiente={!!codigoInvitacion} nombrePrecargado={nombreInvitacion} />
  }

  // Gates del flujo de invitación por mail (Bloque 2.1b) — todos antes de
  // montar WorkspaceProvider/AppLogueado, para no dejar ver la app logueada
  // "de fondo" mientras la invitación todavía se está resolviendo.
  if (codigoInvitacion && procesandoInvitacion) {
    return (
      <div className="app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--gris)', fontSize: 14 }}>Uniéndote a tu espacio...</p>
      </div>
    )
  }

  if (errorInvitacion?.tipo === 'email_no_coincide') {
    return (
      <div className="app" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, gap: 16, textAlign: 'center' }}>
        <p style={{ fontSize: 15 }}>Estás conectada como <strong>{usuario.email}</strong> — cerrá sesión para aceptar esta invitación.</p>
        <button className="btn-primario" onClick={handleLogout}>Cerrar sesión</button>
      </div>
    )
  }

  if (errorInvitacion?.tipo === 'reintentable') {
    return (
      <div className="app" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, gap: 16, textAlign: 'center' }}>
        <p style={{ fontSize: 15 }}>{errorInvitacion.mensaje}</p>
        <button className="btn-primario" onClick={() => { setProcesandoInvitacion(true); setErrorInvitacion(null); setIntentoInvitacion(n => n + 1) }}>Reintentar</button>
      </div>
    )
  }

  if (errorInvitacion?.tipo === 'terminal') {
    return (
      <div className="app" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, gap: 16, textAlign: 'center' }}>
        <p style={{ fontSize: 15 }}>{errorInvitacion.mensaje}</p>
        <button className="btn-primario" onClick={() => setErrorInvitacion(null)}>Continuar</button>
      </div>
    )
  }

  return (
    <WorkspaceProvider usuario={usuario}>
      <AppLogueado
        usuario={usuario}
        tab={tab}
        setTab={setTab}
        recetaSeleccionada={recetaSeleccionada}
        volver={volver}
        agregarACompras={agregarACompras}
        agregarAlMenu={agregarAlMenu}
        favoritos={favoritos}
        toggleFavorito={toggleFavorito}
        menuRecetas={menuRecetas}
        listaCompras={listaCompras}
        verReceta={verReceta}
        removerDelMenu={removerDelMenu}
        marcarRealizada={marcarRealizada}
        setListaCompras={setListaCompras}
        removerReceta={removerReceta}
        historial={historial}
        favoritosDetalle={favoritosDetalle}
        handleLogout={handleLogout}
        handleEliminarCuenta={handleEliminarCuenta}
        setUsuario={setUsuario}
        trigNuevoRegistroProgreso={trigNuevoRegistroProgreso}
        setTrigNuevoRegistroProgreso={setTrigNuevoRegistroProgreso}
      />
    </WorkspaceProvider>
  )
}

function AppLogueado({
  usuario, tab, setTab, recetaSeleccionada, volver, agregarACompras, agregarAlMenu,
  favoritos, toggleFavorito, menuRecetas, listaCompras, verReceta, removerDelMenu, marcarRealizada,
  setListaCompras, removerReceta, historial, favoritosDetalle, handleLogout, handleEliminarCuenta, setUsuario,
  trigNuevoRegistroProgreso, setTrigNuevoRegistroProgreso
}) {
  const { cargandoWorkspace, workspaceActivo, esSuperAdmin, esComercial } = useWorkspace()
  const [perfilAbierto, setPerfilAbierto] = useState(false)
  const [verComoPaciente, setVerComoPaciente] = useState(false)

  if (cargandoWorkspace) {
    return (
      <div className="app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--gris)', fontSize: 14 }}>Cargando...</p>
      </div>
    )
  }

  // Gate de rol (Bloque 2.1a, extendido en #56 para paridad de staff, y en
  // el Bloque 4.1 para el equipo comercial): owner/staff/super-admin/
  // comercial van al panel tipo ERP (AdminApp, sidebar+topbar+router
  // propio), paciente sigue en la mobile de siempre. No depende de tener
  // workspace activo resuelto (un super-admin o comercial puede no
  // pertenecer a ningún workspace propio — su router interno maneja esa
  // ruta sin necesitarlo). "Ver como paciente" es un override de sesión,
  // no cambia el rol real — mismo método de QA que se usó en todos los
  // checkpoints de este proyecto hasta ahora.
  const esAdmin = !verComoPaciente && (esSuperAdmin || esComercial || ['owner', 'staff'].includes(workspaceActivo?.rolEnWorkspace))

  if (esAdmin) {
    return (
      <Suspense fallback={null}>
        <AdminApp
          usuario={usuario}
          esSuperAdmin={esSuperAdmin}
          esComercial={esComercial}
          onVerComoPaciente={() => setVerComoPaciente(true)}
          onLogout={handleLogout}
        />
      </Suspense>
    )
  }

  // A partir de acá: paciente real, o super-admin que activó "ver como
  // paciente" — en los dos casos la simulación tiene que ser fiel, así que
  // nunca se expone esSuperAdmin real en la UI mobile de abajo (si no, "ver
  // como paciente" no ocultaría nada). Siempre da false por construcción:
  // si esSuperAdmin fuera true sin verComoPaciente, ya se hubiera retornado
  // arriba.
  const esSuperAdminEnMobile = false

  // Sin workspace activo resuelto: o pertenece a varios y tiene que elegir,
  // o no pertenece a ninguno todavía (ver SelectorWorkspace).
  if (!workspaceActivo) {
    return <SelectorWorkspace onLogout={handleLogout} esSuperAdmin={esSuperAdminEnMobile} onIrPanelSuperAdmin={() => setTab('super-admin')} onIrReportes={() => setTab('reportes')} />
  }

  if (recetaSeleccionada) {
    return (
      <div className="app">
        <Receta
          receta={recetaSeleccionada}
          onVolver={volver}
          onAgregarCompras={agregarACompras}
          onAgregarMenu={agregarAlMenu}
          esFavorito={favoritos.has(recetaSeleccionada.id)}
          onToggleFavorito={() => toggleFavorito(recetaSeleccionada)}
        />
      </div>
    )
  }

  if (tab === 'reportes') {
    return (
      <div className="app">
        <Reportes usuario={usuario} onVolver={() => { setTab('inicio'); setPerfilAbierto(true) }} />
        <NavBottom tab={tab} setTab={setTab} esSuperAdmin={esSuperAdminEnMobile} menuRecetas={menuRecetas} listaCompras={listaCompras} />
      </div>
    )
  }

  if (tab === 'configuracion-paciente') {
    return (
      <div className="app">
        <ConfiguracionPaciente
          usuario={usuario}
          onIrAPerfil={() => { setTab('inicio'); setPerfilAbierto(true) }}
          onIrAInicio={() => setTab('inicio')}
          onIrAHabitos={() => setTab('habitos')}
          onIrARegistrarProgreso={() => { setTrigNuevoRegistroProgreso(n => n + 1); setTab('progreso') }}
        />
        <NavBottom tab={tab} setTab={setTab} esSuperAdmin={esSuperAdminEnMobile} menuRecetas={menuRecetas} listaCompras={listaCompras} />
      </div>
    )
  }

  if (tab === 'sos') {
    return (
      <div className="app">
        <Sos usuario={usuario} onVolver={() => setTab('inicio')} />
        <NavBottom tab={tab} setTab={setTab} esSuperAdmin={esSuperAdminEnMobile} menuRecetas={menuRecetas} listaCompras={listaCompras} />
      </div>
    )
  }

  return (
    <div className="app">
      <button className="perfil-trigger" onClick={() => setPerfilAbierto(true)} aria-label="Perfil">
        <User size={18} strokeWidth={2} />
      </button>

      {verComoPaciente && (
        <button className="volver-admin-banner" onClick={() => setVerComoPaciente(false)}>
          Viendo como paciente · Volver al panel admin
        </button>
      )}

      <div className="contenido">
        {tab === 'inicio' && <Inicio usuario={usuario} onNavegar={setTab} />}
        {tab === 'mealprep' && (
          <Mealprep
            menuRecetas={menuRecetas}
            listaCompras={listaCompras}
            favoritos={favoritos}
            onVerReceta={verReceta}
            onRemoverDeMenu={removerDelMenu}
            onMarcarRealizada={marcarRealizada}
            onLimpiarCompras={() => setListaCompras([])}
            onRemoverReceta={removerReceta}
          />
        )}
        {tab === 'progreso' && <Progreso usuario={usuario} abrirRegistroTrigger={trigNuevoRegistroProgreso} />}
        {tab === 'clases' && <Clases usuario={usuario} esAdmin={esSuperAdminEnMobile} />}
        {tab === 'plan' && <PlanLineamientos usuario={usuario} />}
        {tab === 'diario' && <Diario usuario={usuario} />}
        {tab === 'habitos' && <Habitos usuario={usuario} />}
        {tab === 'accesos' && (
          <Accesos esSuperAdmin={esSuperAdminEnMobile} workspaceActivo={workspaceActivo} onNavegar={setTab} />
        )}
        {tab === 'turnos' && <Turnos usuario={usuario} onVolver={() => setTab('inicio')} />}
        {tab === 'admin' && esSuperAdminEnMobile && <AdminRecetarios usuario={usuario} />}
      </div>

      <Perfil
        abierto={perfilAbierto}
        onCerrar={() => setPerfilAbierto(false)}
        usuario={usuario}
        historial={historial}
        favoritos={favoritos}
        favoritosDetalle={favoritosDetalle}
        onVerReceta={verReceta}
        onLogout={handleLogout}
        onEliminarCuenta={handleEliminarCuenta}
        onNavegar={setTab}
        onActualizarUsuario={setUsuario}
      />

      <NavBottom tab={tab} setTab={setTab} esSuperAdmin={esSuperAdminEnMobile} menuRecetas={menuRecetas} listaCompras={listaCompras} />
    </div>
  )
}

const TABS_ACCESOS = ['progreso', 'clases', 'plan', 'turnos', 'admin']

function NavBottom({ tab, setTab, menuRecetas, listaCompras }) {
  return (
    <nav className="nav-bottom">
      <button className={`nav-btn ${tab === 'inicio' ? 'active' : ''}`} onClick={() => setTab('inicio')}>
        <Home className="nav-icon" size={20} strokeWidth={2} />
        <span className="nav-label">Inicio</span>
      </button>
      <button className={`nav-btn ${tab === 'mealprep' ? 'active' : ''}`} onClick={() => setTab('mealprep')}>
        <Salad className="nav-icon" size={20} strokeWidth={2} />
        <span className="nav-label">Recetas</span>
        {(menuRecetas.length > 0 || listaCompras.length > 0) && (
          <span className="nav-badge">{menuRecetas.length + new Set(listaCompras.map(i => i.origen)).size}</span>
        )}
      </button>
      <button className={`nav-btn nav-btn-diario ${tab === 'diario' ? 'active' : ''}`} onClick={() => setTab('diario')}>
        <span className="nav-icon-circulo"><NotebookText size={22} strokeWidth={2} color="white" /></span>
        <span className="nav-label">Registro</span>
      </button>
      <button className={`nav-btn ${tab === 'sos' ? 'active' : ''}`} onClick={() => setTab('sos')}>
        <LifeBuoy className="nav-icon" size={20} strokeWidth={2} />
        <span className="nav-label">S.O.S.</span>
      </button>
      <button className={`nav-btn ${tab === 'accesos' || TABS_ACCESOS.includes(tab) ? 'active' : ''}`} onClick={() => setTab('accesos')}>
        <MenuIcon className="nav-icon" size={20} strokeWidth={2} />
        <span className="nav-label">Menú</span>
      </button>
    </nav>
  )
}
